
function normalize(value = '') {
  return String(value).trim().toLocaleLowerCase('az-AZ')
    .replace(/[əışçöüğ]/g, c => ({ ə: 'e', ı: 'i', ş: 's', ç: 'c', ö: 'o', ü: 'u', ğ: 'g' }[c]))
    .replace(/[^a-z0-9]/g, '');
}
const aliases = {
  baku: 'baki', ganja: 'gence', ganca: 'gence',
  qurdqapisiqebiristanligi: 'yasamalqebiristanligiqurdqapisi',
  qurdqapisi: 'yasamalqebiristanligiqurdqapisi',
  yasamalqebiristanligi: 'yasamalqebiristanligiqurdqapisi',
  qurdqapisiyasamalqebiristanligi: 'yasamalqebiristanligiqurdqapisi',
  imamzadekompleksi: 'imamzadeqebiristanligi',
  imamzadekompleksiveqebiristanligi: 'imamzadeqebiristanligi',
  imamzadekompleksindekiqebiristanliq: 'imamzadeqebiristanligi',
  gencesebzikarqebiristanligi: 'sebzikarqebiristanligi',
  sebiskarcemetery: 'sebiskarqebiristanligi',
};
const key = value => {
  const normalized = normalize(value).replaceAll('qebristanligi', 'qebiristanligi');
  return aliases[normalized] || normalized;
};
const nameOf = record => record.name || record.name_az || record.nameEn || '';

// Count references; never rewrite their names, coordinates, or catalog IDs.
function referenceCount(records, field, id) {
  return records.filter(record => record[field] === id ||
    record.location?.[field] === id || record.graveLocation?.[field] === id ||
    (Array.isArray(record[field + 's']) && record[field + 's'].includes(id))).length;
}

function buildCatalogSync(cities, cemeteries, references, finalCatalog) {
  // Approved temporary exception: distinct GPS location with a working map.
  const catalog = { ...finalCatalog, 'Gəncə': [...finalCatalog['Gəncə'], 'Səbiskar qəbiristanlığı'] };
  if (!Array.isArray(references)) throw new Error('Reference inspection is required before catalog synchronization.');
  const writes = [];
  const selectedCities = new Map();
  const chosenCemeteries = new Set();
  const chosenCities = new Set();
  let kept = 0;
  const add = (collection, id, data, previous) => {
    const changed = !previous || Object.entries(data).some(([k, v]) => previous[k] !== v);
    if (changed) writes.push({ collection, id, data, exists: !!previous });
  };
  const cemeteryCityKey = record => {
    const city = cities.find(c => c.id === record.cityId);
    if (city) return key(nameOf(city));
    if (record.cityName) return key(record.cityName);
    if (Object.keys(catalog).some(name => key(name) === key(record.cityId))) return key(record.cityId);
    // Recover orphaned legacy city IDs only for an unambiguous exact catalog name.
    const matches = Object.entries(catalog).filter(([, names]) => names.some(name => key(name) === key(nameOf(record))));
    return matches.length === 1 ? key(matches[0][0]) : '';
  };
  for (const cityName of Object.keys(catalog)) {
    const matching = cities.filter(c => key(nameOf(c)) === key(cityName) || c.id === 'city_' + normalize(cityName));
    matching.sort((a, b) => referenceCount(references, 'cityId', b.id) - referenceCount(references, 'cityId', a.id) || a.id.localeCompare(b.id));
    const city = matching[0];
    const id = city?.id || 'city_' + normalize(cityName);
    selectedCities.set(key(cityName), id);
    chosenCities.add(id);
    add('cities', id, { name: cityName, name_az: cityName, isActive: true, isOfficial: true, catalogVersion: 2 }, city);
  }
  for (const [cityName, names] of Object.entries(catalog)) {
    const cityId = selectedCities.get(key(cityName));
    for (const name of names) {
      const matches = cemeteries.filter(c => {
        return key(nameOf(c)) === key(name) &&
          cemeteryCityKey(c) === key(cityName);
      });
      // Preserve any real map configuration when selecting a duplicate survivor.
      matches.sort((a, b) => Number(!!(b.mapAsset || b.planAsset || b.internalMapKey || b.graveMapAvailable)) - Number(!!(a.mapAsset || a.planAsset || a.internalMapKey || a.graveMapAvailable)) ||
        referenceCount(references, 'cemeteryId', b.id) - referenceCount(references, 'cemeteryId', a.id) || a.id.localeCompare(b.id));
      const existing = matches[0];
      const id = existing?.id || 'cemetery_' + normalize(cityName) + '_' + normalize(name);
      chosenCemeteries.add(id);
      if (existing) kept++;
      const data = { name, name_az: name, cityName, cityId, isActive: true, isOfficial: true, catalogVersion: 2 };
      if (key(name) === 'yasamalqebiristanligiqurdqapisi' && key(cityName) === 'baki') {
        data.internalMapKey = 'qurd_qapisi';
        data.graveMapAvailable = true;
      }
      if (key(name) === 'sebiskarqebiristanligi' && key(cityName) === 'gence') {
        data.internalMapKey = 'sebiskar';
        data.graveMapAvailable = true;
        data.catalogException = true;
      }
      if (!existing && !data.internalMapKey) data.graveMapAvailable = false;
      add('cemeteries', id, data, existing);
    }
  }
  const extras = [];
  for (const [collection, records, selected, field] of [
    ['cities', cities, chosenCities, 'cityId'],
    ['cemeteries', cemeteries, chosenCemeteries, 'cemeteryId'],
  ]) {
    for (const record of records) {
      if (selected.has(record.id)) continue;
      extras.push({ collection, id: record.id, name: nameOf(record), references: referenceCount(references, field, record.id) });
      add(collection, record.id, { isActive: false, isOfficial: false, catalogVersion: 2 }, record);
    }
  }
  // Refuse deterministic-ID collisions rather than overwriting unrelated data.
  for (const write of writes) {
    if (!write.exists && (write.collection === 'cities' ? cities : cemeteries).some(r => r.id === write.id)) {
      throw new Error('Catalog ID collision: ' + write.id);
    }
  }
  return { writes, extras, kept, addedCities: writes.filter(w => w.collection === 'cities' && !w.exists).length,
    addedCemeteries: writes.filter(w => w.collection === 'cemeteries' && !w.exists).length,
    deactivated: writes.filter(w => w.data.isActive === false).length };
}
export { buildCatalogSync, referenceCount, normalize, nameOf };
